import React, { useState } from 'react';
import {
  Search,
  Layers,
  Table,
  Kanban,
  User,
  Truck,
  MapPin,
  Tag,
  Calendar,
  Archive,
  ArrowUpDown,
  LayoutGrid,
  Filter,
  Columns,
  X,
  Plus,
  Info,
} from 'lucide-react';
import { AUTHORIZED_SALESPERSONS, SERVICE_TYPES, OUTBOUND_STATES } from '../types';

interface QuickFiltersProps {
  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;

  // View Mode Switcher
  activeTab: string;
  layoutMode: 'pipeline' | 'table' | 'kanban';
  onChangeLayoutMode: (mode: 'pipeline' | 'table' | 'kanban') => void;

  // Quick Filters
  ownerOptions?: string[];
  selectedOwner: string;
  onSelectOwner: (owner: string) => void;
  selectedService: string;
  onSelectService: (service: string) => void;
  selectedState: string;
  onSelectState: (state: string) => void;
  selectedStatus: string;
  onSelectStatus: (status: string) => void;
  selectedLastUpdate?: string;
  onSelectLastUpdate?: (lastUpdate: string) => void;
  customStartDate?: string;
  onSelectCustomStart?: (date: string) => void;
  customEndDate?: string;
  onSelectCustomEnd?: (date: string) => void;
  includeArchived?: boolean;
  onToggleIncludeArchived?: (val: boolean) => void;

  // Sort & Grouping
  sortField?: string;
  onSelectSortField?: (field: 'default' | 'name' | 'dealValue' | 'followUpDate' | 'lastContacted' | 'status' | string) => void;
  sortDir?: 'asc' | 'desc';
  onSelectSortDir?: (dir: 'asc' | 'desc') => void;
  groupBy?: string;
  onSelectGroupBy?: (group: 'pipeline' | 'status' | 'owner' | 'service' | 'industry' | 'lastUpdateDate') => void;

  // Modals & Clear
  onOpenAdvancedFilters: () => void;
  onOpenColumnCustomizer?: () => void;
  onResetFilters: () => void;
  activeFilterCount: number;

  // Auto-refresh & Lead Creation
  autoRefreshEnabled?: boolean;
  onToggleAutoRefresh?: () => void;
  autoRefreshCountdown?: number;
  onAddNewLead?: () => void;
}

export default function QuickFiltersBar({
  searchQuery,
  onSearchChange,
  activeTab,
  layoutMode,
  onChangeLayoutMode,
  ownerOptions,
  selectedOwner,
  onSelectOwner,
  selectedService,
  onSelectService,
  selectedState,
  onSelectState,
  selectedStatus,
  onSelectStatus,
  selectedLastUpdate = '',
  onSelectLastUpdate,
  customStartDate = '',
  onSelectCustomStart,
  customEndDate = '',
  onSelectCustomEnd,
  includeArchived = false,
  onToggleIncludeArchived,
  sortField = 'default',
  onSelectSortField,
  sortDir = 'asc',
  onSelectSortDir,
  groupBy = 'pipeline',
  onSelectGroupBy,
  onOpenAdvancedFilters,
  onOpenColumnCustomizer,
  onResetFilters,
  activeFilterCount,
  autoRefreshEnabled = true,
  onToggleAutoRefresh,
  autoRefreshCountdown = 30,
  onAddNewLead,
}: QuickFiltersProps) {
  const [openMenu, setOpenMenu] = useState<'sort' | 'group' | null>(null);

  return (
    <div className="bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800/80 px-4 md:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 shrink-0 shadow-sm z-20 sticky top-0 transition-colors">
      
      {/* LEFT & CENTER CONTROLS (Search, View Switcher, Quick Filters) */}
      <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-0">
        
        {/* Search Input */}
        <div className="relative w-full sm:w-72 md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            placeholder="Search leads (Name, Company, Owner, Service, Industry)..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 bg-slate-100/70 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-850 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-950 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 shadow-inner"
          />
        </div>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-0.5 hidden lg:block" />

        {/* View Mode Switcher */}
        {activeTab === 'dashboard' && (
          <div className="flex items-center gap-1 shrink-0">
            <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl border border-slate-200/60 dark:border-slate-800 shadow-inner">
              <button
                type="button"
                onClick={() => onChangeLayoutMode('pipeline')}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black transition cursor-pointer ${
                  layoutMode === 'pipeline'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
                title="Grouped Pipeline View"
              >
                <Layers className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Pipeline View</span>
              </button>

              <button
                type="button"
                onClick={() => onChangeLayoutMode('table')}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black transition cursor-pointer ${
                  layoutMode === 'table'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
                title="Spreadsheet Table View"
              >
                <Table className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Table View</span>
              </button>

              <button
                type="button"
                onClick={() => onChangeLayoutMode('kanban')}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black transition cursor-pointer ${
                  layoutMode === 'kanban'
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
                title="Kanban Board View"
              >
                <Kanban className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Kanban Board</span>
              </button>
            </div>
          </div>
        )}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-800 mx-0.5 hidden xl:block" />

        {/* Quick Filter Dropdowns Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Owner Filter */}
          <div className="relative">
            <select
              value={selectedOwner}
              onChange={(e) => onSelectOwner(e.target.value)}
              className={`text-xs font-semibold py-1.5 pl-7 pr-3 rounded-lg border appearance-none cursor-pointer transition ${
                selectedOwner
                  ? 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200 font-bold'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <option value="">All Owners</option>
              {(ownerOptions && ownerOptions.length > 0 ? ownerOptions : AUTHORIZED_SALESPERSONS).map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </select>
            <User className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>

          {/* Service Filter */}
          <div className="relative">
            <select
              value={selectedService}
              onChange={(e) => onSelectService(e.target.value)}
              className={`text-xs font-semibold py-1.5 pl-7 pr-3 rounded-lg border appearance-none cursor-pointer transition ${
                selectedService
                  ? 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200 font-bold'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <option value="">All Services</option>
              {SERVICE_TYPES.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
            <Truck className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>

          {/* Outbound State Filter */}
          <div className="relative">
            <select
              value={selectedState}
              onChange={(e) => onSelectState(e.target.value)}
              className={`text-xs font-semibold py-1.5 pl-7 pr-3 rounded-lg border appearance-none cursor-pointer transition ${
                selectedState
                  ? 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200 font-bold'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <option value="">All States</option>
              {OUTBOUND_STATES.map((st) => (
                <option key={st} value={st}>
                  Outbound {st}
                </option>
              ))}
            </select>
            <MapPin className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => onSelectStatus(e.target.value)}
              className={`text-xs font-semibold py-1.5 pl-7 pr-3 rounded-lg border appearance-none cursor-pointer transition ${
                selectedStatus
                  ? 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200 font-bold'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <option value="">All Statuses</option>
              <option value="Lead">Lead</option>
              <option value="Quoted">Quoted</option>
              <option value="Approved">Approved</option>
              <option value="Finalizing">Finalizing</option>
              <option value="Complete">Complete</option>
              <option value="Transferred">Transferred</option>
              <option value="Declined">Declined</option>
              <option value="Not Awarded">Not Awarded</option>
              <option value="30-Day Unresponsive">30-Day Unresponsive</option>
            </select>
            <Tag className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>

          {/* Last Update Date Filter */}
          <div className="relative">
            <select
              value={selectedLastUpdate}
              onChange={(e) => onSelectLastUpdate?.(e.target.value)}
              className={`text-xs font-semibold py-1.5 pl-7 pr-3 rounded-lg border appearance-none cursor-pointer transition ${
                selectedLastUpdate
                  ? 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200 font-bold'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
              }`}
            >
              <option value="">All Update Dates</option>
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last Month (30 Days)</option>
              <option value="custom">Custom Date Range...</option>
            </select>
            <Calendar className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-blue-500 pointer-events-none" />
          </div>

          {/* Custom Date Range Pickers */}
          {selectedLastUpdate === 'custom' && (
            <div className="flex items-center gap-1.5 bg-blue-50/90 dark:bg-blue-950/60 px-2 py-1 rounded-lg border border-blue-300 dark:border-blue-800 animate-fade-in">
              <span className="text-[11px] font-bold text-blue-800 dark:text-blue-300">From:</span>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => onSelectCustomStart?.(e.target.value)}
                className="text-xs font-semibold px-2 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 cursor-pointer"
              />
              <span className="text-[11px] font-bold text-blue-800 dark:text-blue-300">To:</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => onSelectCustomEnd?.(e.target.value)}
                className="text-xs font-semibold px-2 py-0.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 cursor-pointer"
              />
            </div>
          )}

          {/* Include Archived Toggle */}
          <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer select-none transition ${
            includeArchived
              ? 'bg-purple-50 border-purple-300 text-purple-900 dark:bg-purple-950/60 dark:border-purple-800 dark:text-purple-200'
              : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
          }`}>
            <Archive className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
            <span>Include Archived</span>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onToggleIncludeArchived?.(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer ml-0.5"
            />
          </label>
        </div>

        {/* Sort & Group Dropdowns */}
        <div className="flex items-center gap-1.5">
          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === 'sort' ? null : 'sort')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-extrabold rounded-lg border transition-all cursor-pointer ${
                sortField !== 'default'
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-850'
              }`}
            >
              <ArrowUpDown className="w-3.5 h-3.5" /> Sort
            </button>
            {openMenu === 'sort' && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                <div className="absolute left-0 mt-2 w-52 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-1.5">
                  {([
                    { v: 'default', label: 'Default (Alerts & New Leads First)' },
                    { v: 'status', label: 'Status (Lead First)' },
                    { v: 'name', label: 'Name / Company' },
                    { v: 'dealValue', label: 'Deal Value' },
                    { v: 'followUpDate', label: 'Follow-up Date' },
                    { v: 'lastContacted', label: 'Last Interaction' },
                  ] as const).map(({ v, label }) => (
                    <button
                      key={v}
                      onClick={() => {
                        onSelectSortField?.(v);
                        setOpenMenu(null);
                      }}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${sortField === v ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-bold' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                    >
                      {label}
                    </button>
                  ))}
                  <div className="border-t border-slate-100 dark:border-slate-800 mt-1 pt-1 flex gap-1 px-1">
                    <button
                      onClick={() => onSelectSortDir?.('asc')}
                      className={`flex-1 text-[11px] font-bold py-1 rounded cursor-pointer ${sortDir === 'asc' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                    >
                      Asc ↑
                    </button>
                    <button
                      onClick={() => onSelectSortDir?.('desc')}
                      className={`flex-1 text-[11px] font-bold py-1 rounded cursor-pointer ${sortDir === 'desc' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                    >
                      Desc ↓
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Group By Dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenMenu(openMenu === 'group' ? null : 'group')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-extrabold rounded-lg border transition-all cursor-pointer ${
                groupBy !== 'pipeline'
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-850'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Group By
            </button>
            {openMenu === 'group' && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
                <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 p-1.5">
                  {([
                    { v: 'pipeline', label: 'Pipeline (default)' },
                    { v: 'status', label: 'Status' },
                    { v: 'owner', label: 'Owner' },
                    { v: 'service', label: 'Service' },
                    { v: 'industry', label: 'Industry' },
                    { v: 'lastUpdateDate', label: 'Last Update Date' },
                  ] as const).map(({ v, label }) => (
                    <button
                      key={v}
                      onClick={() => {
                        onSelectGroupBy?.(v);
                        setOpenMenu(null);
                      }}
                      className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${groupBy === v ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-bold' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Show/Hide Columns Button */}
          {onOpenColumnCustomizer && (
            <button
              onClick={onOpenColumnCustomizer}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 border border-blue-600 rounded-lg text-xs font-bold text-white transition cursor-pointer shadow-sm"
              title="Show/Hide Columns"
            >
              <Columns className="w-3.5 h-3.5 text-white" /> Show/Hide Columns
            </button>
          )}

          {/* Advanced Logic Rules Button */}
          <button
            onClick={onOpenAdvancedFilters}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition cursor-pointer border border-slate-200 dark:border-slate-700 shrink-0"
          >
            <Filter className="w-3.5 h-3.5 text-blue-600" /> Advanced Rules
          </button>

          {/* Reset Active Filters Button */}
          {activeFilterCount > 0 && (
            <button
              onClick={onResetFilters}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-rose-600 hover:text-rose-800 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition cursor-pointer"
              title="Clear all active quick filters"
            >
              <X className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* RIGHT ACTIONS (Add New Lead Button) */}
      <div className="flex items-center gap-2.5 shrink-0">
        {onAddNewLead && (
          <button
            onClick={onAddNewLead}
            className="flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-650 hover:from-blue-700 hover:to-indigo-750 text-white rounded-xl text-xs font-black hover:shadow-md hover:shadow-blue-500/10 active:scale-[0.98] transition cursor-pointer shrink-0"
          >
            <Plus className="w-3.5 h-3.5" /> Add New CRM Lead
          </button>
        )}
      </div>
    </div>
  );
}
